import React, { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Button, Form, Modal, OverlayTrigger, Popover } from "react-bootstrap";
import ImportIcon from "../../assets/icons/icon_save-01.svg2";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import FullScreenLoadingIndicator from "../Common/FullScreenLoadingIndicator";
import MyPagination from "../Common/MyPagination";
import {
  faEdit,
  faEnvelope,
  faMessage,
  faPhone,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import MySearch from "../Common/MySearch";
import { useDispatch } from "react-redux";
import MyListContainer from "../Common/MyListContainer";
import { toast } from "react-toastify";
import SingleContactCard from "../Contacts/SingleContactCard";
import WhiteButton from "../Common/WhiteButton";
import {
  useGetContactFieldsQuery,
  useLazyGetTempFileInfoQuery,
  useLazyGetTempRecordsQuery,
  useUploadCsvMutation,
  useProcessTempFileMutation,
  useUpdateTempRecordMutation,
  useDeleteTempRecordsMutation,
} from "../store/slices/contactsSlice";
import HelpButton from "../HelpContent/HelpButton";
import { ImportFileAsContacts } from "./ImportFileAsContacts";

const importContactsImportRules = [
  "Column order does not matter; headers are matched by name during import.",
  "Choose one or more primary columns to match existing contacts.",
  "Email values are lowercased; phone numbers are validated with the selected default country.",
  "Additional selected columns update matching contact fields by header name.",
  "Columns that do not match existing contact fields can be created as new custom fields during review.",
  "Status, Groups, Assets, Country, Time-Zone, Latitude, and Longitude have special import handling.",
  "Duplicate source rows or rows matching multiple contacts are flagged for review before import.",
  "If contacts already exist, export and download the current contacts file to use as a reference.",
];

type ContactFieldDefinition = {
  label: string;
  isPrimary?: boolean;
  isEmail?: boolean;
  isSms?: boolean;
  isVoice?: boolean;
  isHidden?: boolean;
  isSelect?: boolean;
  selectType?: string;
  isDateTime?: boolean;
  dateTimeType?: string;
  options?: { label: string }[];
};

const importContactsReservedColumns = [
  "Status",
  "Groups",
  "Assets",
  "Latitude",
  "Longitude",
];

function csvEscape(value: string) {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function getExampleValue(field: ContactFieldDefinition, rowIndex: number) {
  if (field.isEmail) {
    return rowIndex === 0
      ? "alex.chen@example.com"
      : "sam.rivera@example.com";
  }
  if (field.isSms || field.isVoice) {
    return rowIndex === 0 ? "+447700900123" : "+447700900124";
  }
  if (field.isSelect && !field.selectType) {
    return field.options?.[rowIndex]?.label || field.options?.[0]?.label || "Option";
  }
  if (field.isDateTime) {
    if (field.dateTimeType === "time") return rowIndex === 0 ? "09:30" : "14:15";
    if (field.dateTimeType === "date") {
      return rowIndex === 0 ? "2026-07-22" : "2026-07-23";
    }
    return rowIndex === 0 ? "2026-07-22 09:30" : "2026-07-23 14:15";
  }
  if (/first/i.test(field.label)) return rowIndex === 0 ? "Alex" : "Sam";
  if (/last|surname/i.test(field.label)) return rowIndex === 0 ? "Chen" : "Rivera";
  return rowIndex === 0 ? `Example ${field.label}` : `Second ${field.label}`;
}

function getSystemExampleValue(column: string, rowIndex: number) {
  const values: { [key: string]: string[] } = {
    Status: ["active", "active"],
    Groups: ["Customers, Newsletter", "Prospects"],
    Assets: ["Laptop A, Desk Phone", "Tablet"],
    Country: ["United Kingdom", "United Kingdom"],
    "Time-Zone": ["Europe/London", "Europe/London"],
    Latitude: ["51.5074", "53.4808"],
    Longitude: ["-0.1278", "-2.2426"],
  };
  return values[column]?.[rowIndex] || "";
}

function buildImportContactsExampleRows(
  contactFields: ContactFieldDefinition[]
) {
  const visibleFields = contactFields.filter((field) => field.label && !field.isHidden);
  const primaryFields = visibleFields.filter((field) => field.isPrimary).slice(0, 2);
  const additionalFields = visibleFields
    .filter(
      (field) =>
        !field.isPrimary &&
        field.selectType !== "country" &&
        field.selectType !== "time-zone"
    )
    .slice(0, Math.max(0, 5 - primaryFields.length));
  const countryColumn = visibleFields.some((field) => field.selectType === "country")
    ? ["Country"]
    : [];
  const timeZoneColumn = visibleFields.some((field) => field.selectType === "time-zone")
    ? ["Time-Zone"]
    : [];
  const fieldColumns = [...primaryFields, ...additionalFields];
  const headers = [
    ...fieldColumns.map((field) => field.label),
    ...importContactsReservedColumns,
    ...countryColumn,
    ...timeZoneColumn,
  ];

  return [
    headers,
    [
      ...fieldColumns.map((field) => getExampleValue(field, 0)),
      ...importContactsReservedColumns.map((column) => getSystemExampleValue(column, 0)),
      ...countryColumn.map((column) => getSystemExampleValue(column, 0)),
      ...timeZoneColumn.map((column) => getSystemExampleValue(column, 0)),
    ],
    [
      ...fieldColumns.map((field) => getExampleValue(field, 1)),
      ...importContactsReservedColumns.map((column) => getSystemExampleValue(column, 1)),
      ...countryColumn.map((column) => getSystemExampleValue(column, 1)),
      ...timeZoneColumn.map((column) => getSystemExampleValue(column, 1)),
    ],
  ];
}

function rowsToCsv(rows: string[][]) {
  return rows.map((row) => row.map(csvEscape).join(",")).join("\n") + "\n";
}

export interface ImportedContactFileInfoType {
  file: string;
  uuid: string;
  createdAt: string;
  lastAccessAt: string;
  createdBy: string;
  status: string;
  header: string[];
  matchBy: string;
  defaultCountryIso: string;
  stats: {
    contacts: number;
    email: number;
    rows: number;
    sms: number;
    voice: number;
  };
}

export interface ImportedRecordType {
  rowIndex: number;
  value: string;
  isEmail: boolean;
  isSms: boolean;
  isVoice: boolean;
  isContact: boolean;
  hasErrors: boolean;
  data: any;
  rowDuplicates: number;
  contactsMatched: number;
  contactUuid: string;
  [key: string]: any; // Add index signature
}

const { countries, defaultCountry, defaultTZ } = JSON.parse(
  (document.querySelector("#app") as any)?.dataset?.page
).props;

function ImportContactsExampleSection({
  contactFields,
}: {
  contactFields: ContactFieldDefinition[];
}) {
  const exampleRows = buildImportContactsExampleRows(contactFields);
  const exampleCsv = rowsToCsv(exampleRows);
  const exampleDownloadHref = `data:text/csv;charset=utf-8,${encodeURIComponent(
    exampleCsv
  )}`;

  return (
    <div className="card border-0 mb-3">
      <div className="card-body bg-white rounded-3">
        <div className="d-flex flex-wrap justify-content-between gap-3 mb-3">
          <div>
            <div className="fw-bold">Example CSV for this account</div>
            <div className="text-secondary small">
              This example uses your account contact field names as headers.
              Select primary columns after upload, then choose which extra
              fields to import. Column order does not matter.
            </div>
          </div>
          <a
            className="btn btn-outline-secondary rounded-pill align-self-start"
            href={exampleDownloadHref}
            download="import-contacts-example.csv"
          >
            Download example CSV
          </a>
        </div>
        <div className="table-responsive mb-3">
          <table className="table table-sm table-bordered mb-0 bg-white small">
            <tbody>
              {exampleRows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell) => (
                    <td
                      key={`${rowIndex}-${cell}`}
                      className={rowIndex === 0 ? "fw-bold" : ""}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="small">
          <div className="fw-bold mb-2">How the import works</div>
          <ul className="mb-0 ps-3">
            {importContactsImportRules.map((rule) => (
              <li key={rule}>{rule}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export const UpdateRecordPopover = ({
  importedFileId,
  row,
  field,
  setForceRefresh,
}: {
  importedFileId: string;
  row: ImportedRecordType;
  field: string;
  setForceRefresh: any;
}) => {
  const [value, setValue] = useState(row.data[field]);
  const [show, setShow] = useState(false);

  const [updateTempRecord] = useUpdateTempRecordMutation();

  const handleRecordUpdate = async (
    row: ImportedRecordType,
    field: string,
    value: string
  ) => {
    try {
      const res = await updateTempRecord({
        file: importedFileId,
        rowIndex: [row.rowIndex || 0],
        data: { [field]: value },
      }).unwrap();
      if (res.message === "no changes") {
        toast.error("No changes were made to the record.");
        return;
      }
      setForceRefresh(new Date().toLocaleString());
      setShow(false);
    } catch {
      toast.error("Failed to update record.");
    }
  };

  return (
    <OverlayTrigger
      trigger="click"
      placement="top"
      rootClose
      onExit={() => setValue(row.data[field])}
      show={show}
      onToggle={() => setShow(!show)}
      overlay={
        <Popover id={`popover-update-record-${row.rowIndex || 0}-${field}`}>
          <Popover.Header as="h3">Update record</Popover.Header>
          <Popover.Body>
            <div className="d-flex">
              <input
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="form-control"
              />
              <button
                className="btn btn-colored"
                onClick={() => {
                  handleRecordUpdate(row, field, value);
                }}
              >
                Update
              </button>
            </div>
          </Popover.Body>
        </Popover>
      }
    >
      <Button variant="link" className="p-1">
        <FontAwesomeIcon icon={faEdit} />
      </Button>
    </OverlayTrigger>
  );
};

export function ImportedRecords({
  importedFileId,
}: {
  importedFileId: string;
}) {
  const [fileInfo, setFileInfo] = useState<ImportedContactFileInfoType>();
  const [recordsData, setRecordsData] = useState<ImportedRecordType[]>();
  const [total, setTotal] = useState(0);
  const [channels, setChannels] = useState(["sms", "email", "voice"]);
  const [withContacts, setWithContacts] = useState(false);
  const [withDuplicatedRows, setWithDuplicatedRows] = useState(false);
  const [withDuplicatedContacts, setWithDuplicatedContacts] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedRecords, setSelectedRecords] = useState<number[]>([]);
  const [forceRefresh, setForceRefresh] = useState<string>(
    new Date().toLocaleString()
  );

  const [fetchRecords] = useLazyGetTempRecordsQuery();
  const [deleteTempRecords] = useDeleteTempRecordsMutation();

  const loadRecords = async (page = 0, perPage = 12) => {
    try {
      const records = await fetchRecords({
        file: importedFileId,
        channels,
        withContacts,
        withDuplicatedRows,
        withDuplicatedContacts,
        search: search !== "" ? search : undefined,
        page,
        perPage,
      }).unwrap();
      setFileInfo(records.file);
      setRecordsData(records.data);
      setTotal(records.total);
    } catch {
      // silently ignore
    }
  };

  const handleDeleteSelected = async () => {
    try {
      await deleteTempRecords({
        file: importedFileId,
        rowIndexes: selectedRecords,
      }).unwrap();
      setForceRefresh(new Date().toLocaleString());
      setSelectedRecords([]);
    } catch {
      toast.error("Failed to delete records.");
    }
  };

  const handleDeleteFiltered = async () => {
    try {
      await deleteTempRecords({
        file: importedFileId,
        withContacts,
        withDuplicatedRows,
        withDuplicatedContacts,
      }).unwrap();
      setForceRefresh(new Date().toLocaleString());
    } catch {
      toast.error("Failed to delete records.");
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      loadRecords();
    }, 300);
    return () => clearTimeout(timeout);
  }, [
    channels,
    withContacts,
    withDuplicatedRows,
    withDuplicatedContacts,
    search,
  ]);

  const columns = (fileInfo?.header || []).filter(
    (h) => h !== fileInfo?.matchBy
  );

  return (
    fileInfo && (
      <>
        <h5 className="mt-3 mb-3">File name: {fileInfo.file}</h5>
        <div className="mb-2">
          <MySearch
            value={search}
            onChange={(e: any) => setSearch(e.target.value)}
            placeholder="Search for a contact"
          />
        </div>
        <div className="d-flex align-items-center">
          <div className="d-flex align-items-center me-3">
            <div className="me-1">Channels:</div>
            <Input
              value={channels}
              onChange={setChannels}
              isMulti
              inputType="select"
              options={["sms", "email", "voice"].map((c) => ({
                label: c,
                value: c,
              }))}
            />
          </div>
          <Form.Check
            id="with-contacts-checkbox"
            type="checkbox"
            label="With contacts"
            checked={withContacts}
            onChange={(e) => setWithContacts(e.target.checked)}
            className="me-3"
          />
          <Form.Check
            id="with-duplicated-rows-checkbox"
            type="checkbox"
            label="With duplicated rows"
            checked={withDuplicatedRows}
            onChange={(e) => setWithDuplicatedRows(e.target.checked)}
            className="me-3"
          />

          <Form.Check
            id="with-duplicated-contacts-checkbox"
            type="checkbox"
            label="With duplicated contacts"
            checked={withDuplicatedContacts}
            onChange={(e) => setWithDuplicatedContacts(e.target.checked)}
            className="me-3"
          />
        </div>

        <div
          className="d-flex justify-content-end align-items-start"
          style={{ minHeight: 40 }}
        >
          {selectedRecords.length > 0 && (
            <button
              className="btn btn-secondary ms-2 px-1 py-0"
              onClick={() => {
                handleDeleteSelected();
              }}
            >
              Delete selected
            </button>
          )}
          {(withContacts || withDuplicatedContacts || withDuplicatedRows) && (
            <button
              className="btn btn-secondary ms-2 px-1 py-0"
              onClick={() => {
                handleDeleteFiltered();
              }}
            >
              Delete filtered
            </button>
          )}
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Select</th>
              <th>Channels</th>
              <th className="text-decoration-underline">{fileInfo.matchBy}</th>
              {columns.map((c: any, idx: React.Key | null | undefined) => (
                <th key={c}>{c}</th>
              ))}

              <th className="text-end"></th>
            </tr>
          </thead>
          <tbody>
            {recordsData?.map((row) => (
              <tr key={row.rowIndex || 0}>
                <td>
                  <Form.Check
                    id={`record-${row.rowIndex || 0}-checkbox`}
                    type="checkbox"
                    checked={selectedRecords.includes(row.rowIndex || 0)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedRecords([
                          ...selectedRecords,
                          row.rowIndex || 0,
                        ]);
                      } else {
                        setSelectedRecords(
                          selectedRecords.filter(
                            (r) => r !== (row.rowIndex || 0)
                          )
                        );
                      }
                    }}
                  />
                </td>
                <td className="p-2 text-nowrap">
                  {
                    // channel icons
                    [
                      { label: "isVoice", icon: faPhone },
                      { label: "isSms", icon: faMessage },
                      { label: "isEmail", icon: faEnvelope },
                    ].map((channel) => (
                      <FontAwesomeIcon
                        key={channel.label}
                        icon={channel.icon}
                        className={`p-0 text-purple me-3 ${
                          (row as any)[channel.label] ? "" : "opacity-25"
                        } `}
                      />
                    ))
                  }
                </td>
                <td>
                  {row.data[fileInfo?.matchBy]}{" "}
                  {row.isContact ? (
                    <SingleContactCard uuid={row.contactUuid} />
                  ) : null}{" "}
                </td>
                {columns.map(
                  (field: any, idx: React.Key | null | undefined) => (
                    <td key={idx}>
                      <span>{row.data[field]}</span>

                      <UpdateRecordPopover
                        importedFileId={importedFileId}
                        row={row}
                        field={field}
                        setForceRefresh={setForceRefresh}
                      />
                    </td>
                  )
                )}
                <td className="text-end">
                  {["rowDuplicates", "contactsMatched"].map(
                    (field) =>
                      !!row[field] && (
                        <OverlayTrigger
                          key={
                            "error-popover-" + field + "-" + (row.rowIndex || 0)
                          }
                          trigger="click"
                          placement="auto"
                          rootClose
                          overlay={
                            <Popover
                              id={
                                "error-popover-contacts-matched" +
                                (row.rowIndex || 0)
                              }
                            >
                              <Popover.Header as="h3">
                                {field === "rowDuplicates"
                                  ? "Duplicated"
                                  : "Matched"}{" "}
                                contact
                              </Popover.Header>
                              <Popover.Body>
                                {field === "rowDuplicates"
                                  ? `This contact field was duplicated in ${row[field]} rows.`
                                  : `This contact was matched by ${row[field]} rows.`}
                              </Popover.Body>
                            </Popover>
                          }
                        >
                          <Button variant="btn btn-sm btn-secondary px-1 py-0 ms-2">
                            {row[field]}{" "}
                            {field === "rowDuplicates"
                              ? "duplicates"
                              : "matches"}
                          </Button>
                        </OverlayTrigger>
                      )
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <MyPagination
          total={total}
          onPaginate={loadRecords}
          forceRefresh={forceRefresh}
        />
      </>
    )
  );
}

export default function ImportContactsModal({
  setFile = () => {},
  button,
  matchByAutomatically = false,
  showImportedFileAfterImport = true,
}: {
  setFile: (f: ImportedContactFileInfoType | undefined) => void;
  button?: React.ReactNode;
  matchByAutomatically?: boolean;
  showImportedFileAfterImport?: boolean;
}) {
  const [show, setShow] = useState(false);
  const [loading, setloading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // step 1: select file
  const [selectedFile, setSelectedFile] = useState<File>();
  const handleSelectFile = (
    e: React.ChangeEvent<HTMLInputElement> | React.DragEvent
  ) => {
    flushSync(() => {
      setloading(true);
    });

    try {
      const f =
        (e as React.DragEvent).dataTransfer?.files?.[0] ||
        (e as React.ChangeEvent<HTMLInputElement>).target?.files?.[0] ||
        undefined;

      if (f) {
        if (f.type !== "text/csv") {
          toast.error("Invalid file type. Please select a CSV file.");
          setSelectedFile(undefined);
          return;
        }
        setSelectedFile(f);
      }
    } finally {
      setloading(false);
    }
  };

  // step 2: import file
  const contactFields = useGetContactFieldsQuery().data || [];
  const [importedFileId, setImportedFileId] = useState("");
  const [importedFields, setImportedFields] = useState([]);
  const [uploadCsv] = useUploadCsvMutation();
  const handleImport = async () => {
    if (selectedFile) {
      setloading(true);
      const bodyContent = new FormData();
      bodyContent.append("file", selectedFile);

      try {
        const imported = await uploadCsv(bodyContent).unwrap();
        if (imported) {
          setImportedFields(imported.header);
          setImportedFileId(imported.file);
          setSelectedFile(undefined);
        }
      } catch {
        toast.error("Failed to upload file.");
      } finally {
        setTimeout(() => setloading(false), 300);
      }
    }
  };

  // step 3: process file
  const [idHeader, setIdHeader] = useState([]);
  const [headers, setHeaders] = useState([]);
  let automaticIdHeaders: string[] = [];
  if (matchByAutomatically && importedFields.length > 0) {
    automaticIdHeaders = importedFields?.filter((h: string) =>
      contactFields.some(
        (field: { label: string; isPrimary: boolean }) =>
          field.label.toLowerCase().trim().replace(/\s+/g, "") ===
            (h.toLowerCase() || "").trim().replace(/\s+/g, "") &&
          field.isPrimary
      )
    );
  }
  const [isProcessed, setIsProcessed] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [defaultCountryIso, setDefaultCountryIso] = useState(
    defaultCountry.country_code
  );
  const dispatch = useDispatch();
  const [checkTempFileInfo] = useLazyGetTempFileInfoQuery();
  const [processTempFile] = useProcessTempFileMutation();

  const handleCheckProcessing = async () => {
    try {
      const fileInfo = await checkTempFileInfo(importedFileId).unwrap();
      if (fileInfo.status === "processed") {
        if (showImportedFileAfterImport === false) {
          setIsProcessing(false);
          toast.success("File processed successfully.");
          setFile(fileInfo);
          setShow(false);
          return;
        }
        setFile(fileInfo);
        setTimeout(() => {
          setIsProcessing(false);
          setIsProcessed(true);
        }, 1000);
        return;
      }
    } catch {
      // keep polling
    }
    setTimeout(() => {
      handleCheckProcessing();
    }, 1000);
  };

  const handleProcessFile = async () => {
    if (importedFileId) {
      setIsProcessing(true);
      try {
        const isQueued = await processTempFile({
          file: importedFileId,
          idHeaders: [...automaticIdHeaders, ...idHeader],
          headers,
          defaultCountryIso,
          parseForImport: matchByAutomatically,
        }).unwrap();
        if (isQueued) {
          setIsProcessed(true);
          handleCheckProcessing();
        }
      } catch {
        setIsProcessing(false);
        toast.error("Failed to process file.");
      }
    }
  };

  // step 4: display records

  const handleShow = () => {
    setShow(true);
    setSelectedFile(undefined);
  };

  const handleClose = () => {
    setShow(false);
  };

  const [hovered, setHovered] = useState(false);

  return (
    <>
      <FullScreenLoadingIndicator loading={loading || isProcessing} />

      <div
        className=""
        onClick={() => {
          setSelectedFile(undefined);
          setImportedFileId("");
          setIsProcessed(false);
          setIsProcessing(false);
          setIdHeader([]);
          setHeaders([]);
          handleShow();
        }}
      >
        {button || (
          <WhiteButton
            as="span"
            heading="Import"
            subheading="file"
            icon={ImportIcon}
            target={undefined}
            disabled={false}
          />
        )}
      </div>

      <Modal
        show={show}
        onHide={handleClose}
        centered
        contentClassName="bg-purple-gradient"
        size="xl"
        enforceFocus={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Import Contacts From File{" "}
            <HelpButton helpKey="import-contacts-from-file" />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!!importedFileId ? (
            <div className="mt-3">
              {isProcessed ? (
                <>
                  {showImportedFileAfterImport && (
                    <>
                      <div className="alert alert-secondary">
                        The file has been processed successfully. You can review
                        the imported records and import them as contacts.
                      </div>
                      <ImportFileAsContacts importedFileId={importedFileId} />
                    </>
                  )}
                </>
              ) : (
                <>
                  <div className="mb-3">
                    The following fields were found in the imported file. Please
                    select the fields that you want to use.
                  </div>
                  {automaticIdHeaders.length > 0 && (
                    <div className="mb-3">
                      <div>
                        The following fields were automatically detected as
                        primary contact fields.
                      </div>
                      <div className="d-flex gap-2 mt-2">
                        {automaticIdHeaders.map((h) => (
                          <span key={h} className="badge badge-sm bg-colored">
                            {h}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="">
                    <div className="d-flex gap-5 mb-2">
                      <div className="">
                        <div className="">
                          Please select the column that contains the{" "}
                          {showImportedFileAfterImport ? (
                            <span>
                              <span className="fw-bold">primary</span> contact
                              fields
                            </span>
                          ) : (
                            <span>
                              comms channels data (phone numbers, emails, etc.)
                            </span>
                          )}
                        </div>
                        <div className="">
                          <Input
                            key={headers.join("")}
                            value={idHeader}
                            onChange={(v: any) => {
                              setIdHeader(v);
                              setHeaders((p: any) =>
                                p.filter((h: string) => !v.includes(h))
                              );
                            }}
                            inputType="select"
                            isMulti
                            options={importedFields
                              .filter((i) => !automaticIdHeaders.includes(i))
                              .filter((i) => !headers.includes(i))
                              .map((i) => ({
                                label: i,
                                value: i,
                              }))}
                          />
                        </div>
                      </div>
                      <div className="">
                        <div className="">
                          Please select the additional columns to import
                        </div>
                        <div className="">
                          <Input
                            key={idHeader.join("")}
                            value={headers}
                            onChange={(v: any) => {
                              if (v.includes("*")) {
                                setHeaders(
                                  importedFields
                                    .filter((i) => !idHeader.includes(i))
                                    .filter(
                                      (i) => !automaticIdHeaders.includes(i)
                                    )
                                );
                                return;
                              }
                              if (v.includes("")) {
                                setHeaders([]);
                                return;
                              }
                              setHeaders(
                                v.filter(
                                  (h: string) =>
                                    !(idHeader as any[]).includes(h)
                                )
                              );
                            }}
                            inputType="select"
                            isMulti
                            options={[
                              { label: "None", value: "" },
                              { label: "All", value: "*" },
                              ...importedFields
                                .filter((i) => !automaticIdHeaders.includes(i))
                                .filter((i) => !(idHeader as any[]).includes(i))
                                .map((i) => ({
                                  label: i,
                                  value: i,
                                })),
                            ]}
                          />
                        </div>
                      </div>
                      <div className="">
                        <div className="">
                          Please select the default country
                        </div>
                        <div className="">
                          <Input
                            value={[defaultCountryIso]}
                            onChange={([v]: any) => {
                              setDefaultCountryIso(v);
                            }}
                            inputType="select"
                            options={countries.map(
                              (c: {
                                country_name: string;
                                country_code: string;
                              }) => ({
                                label: c.country_name,
                                value: c.country_code,
                              })
                            )}
                          />
                        </div>
                      </div>
                      <div className="flex-grow-1 d-flex justify-content-end align-items-end">
                        <button
                          className="btn btn-colored rounded-pill"
                          onClick={handleProcessFile}
                          disabled={!idHeader}
                        >
                          Process
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <>
              <ImportContactsExampleSection contactFields={contactFields} />
              <button
                className={
                  "btn border-2 rounded-3 form-control align-center mb-3 bg-white " +
                  (hovered ? "border-primary" : "")
                }
                style={{ height: 240 }}
                onClick={() => {
                  {
                    (fileInputRef.current as any)?.click();
                  }
                }}
                onDrop={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleSelectFile(e);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setHovered(true);
                }}
                onMouseLeave={() => {
                  setHovered(false);
                }}
                onDragExit={() => {
                  setHovered(false);
                }}
              >
                {selectedFile ? (
                  <div>
                    <div>
                      <span className="text-secondary">Selected file: </span>
                      {selectedFile.name}
                    </div>
                    <div>
                      <span className="text-secondary">File size: </span>
                      {selectedFile.size}
                    </div>
                    <div>
                      <span className="text-secondary">Last modified: </span>
                      {new Date(selectedFile.lastModified).toLocaleString()}
                    </div>
                    <div className="mt-3">
                      <span className="text-secondary">
                        Please click import button or click here to select
                        another file.
                      </span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="mb-3">
                      <ImportIcon width={100} />
                    </div>
                    <div>Drag & drop a file here or click</div>
                  </div>
                )}
              </button>
              <div className="d-flex justify-content-end">
                <button
                  className="btn btn-colored rounded-pill"
                  onClick={handleImport}
                  disabled={!selectedFile}
                >
                  Import
                </button>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      <input
        ref={fileInputRef}
        type="file"
        name="imported-file"
        className="d-none"
        onChange={(e) => {
          handleSelectFile(e);
        }}
      />
    </>
  );
}
